import { isIOSWebview, isWebview } from './webview';

const isAndroidWebview = () => {
  return isWebview() && !isIOSWebview();
};

/**
 * 키보드가 올라온 후 element를 화면 중앙으로 스크롤하는 공통 함수
 */
const scrollToElementWithKeyboard = (element: HTMLElement) => {
  let scrollExecuted = false;

  const smoothScroll = () => {
    if (scrollExecuted) return;
    scrollExecuted = true;
    element.scrollIntoView({
      block: 'center',
      behavior: 'smooth' as ScrollBehavior,
    });
  };

  /* 키보드 등장/사라짐을 visualViewport.resize 로 감지 */
  if ('visualViewport' in window && window.visualViewport) {
    const onResize = () => {
      window.visualViewport?.removeEventListener('resize', onResize);
      // 키보드 애니메이션이 끝난 다음 프레임에서 스크롤
      requestAnimationFrame(smoothScroll);
    };

    window.visualViewport.addEventListener('resize', onResize);

    // Android WebView에서는 resize 이벤트가 발생하지 않을 수 있으므로
    // 타임아웃 폴백을 함께 설정
    if (isAndroidWebview()) {
      // 첫 번째 키보드 등장은 500-700ms 정도 걸릴 수 있음
      // 여러 단계로 스크롤 시도하여 안정성 확보
      const cleanup = () => {
        window.visualViewport?.removeEventListener('resize', onResize);
      };

      // 1차 시도: 350ms (키보드가 이미 캐시된 경우)
      setTimeout(() => {
        if (!scrollExecuted) {
          smoothScroll();
        }
      }, 350);

      // 2차 시도: 600ms (첫 키보드 등장 시)
      setTimeout(() => {
        cleanup();
        if (!scrollExecuted) {
          smoothScroll();
        } else {
          // 이미 스크롤됐어도 위치 재조정 (키보드 높이가 변경될 수 있음)
          element.scrollIntoView({
            block: 'center',
            behavior: 'smooth' as ScrollBehavior,
          });
        }
      }, 600);
    }
  } else {
    // 폴백 – 키보드 애니메이션 평균 시간만큼 지연
    setTimeout(smoothScroll, isAndroidWebview() ? 600 : 350);
  }
};

export const inputFocusHandler = (
  inputRef: React.RefObject<HTMLInputElement>,
) => {
  if (!isWebview()) {
    return;
  }

  const element = inputRef.current;
  if (!element) return;

  element.focus({ preventScroll: true });
  scrollToElementWithKeyboard(element);
};

export const textareaFocusHandler = (
  textareaRefOrElement:
    | React.RefObject<HTMLTextAreaElement>
    | HTMLTextAreaElement
    | null,
) => {
  if (!isWebview()) {
    return;
  }

  // RefObject인지 직접 element인지 확인
  const element =
    textareaRefOrElement instanceof HTMLTextAreaElement
      ? textareaRefOrElement
      : textareaRefOrElement?.current;

  if (!element) return;

  element.focus({ preventScroll: true });
  scrollToElementWithKeyboard(element);
};
